import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, MessageSquare, GitBranch, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/User/Avatar";
import { NeonUpvoteIcon } from "@/components/ui/NeonUpvoteIcon";
import { GeoBadges } from "@/components/GEO/GeoBadges";
import { GeoQualityBadge } from "@/components/GEO/GeoQualityBadge";
import { GeoReliabilityActions } from "@/components/GEO/GeoReliabilityActions";
import { PromptComments } from "@/components/Home/PromptCard/PromptComments";
import { Prompt } from "@/lib/types";
import { calculateGeoQualityScore } from "@/lib/geo-quality-score";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { usePromptActions } from "@/hooks/prompts/use-prompt-actions";
import { useGuestPermissions } from "@/hooks/use-guest-permissions";
import { supabase } from "@/integrations/supabase/client";
import { toDatabaseValue } from "@/utils/supabase/type-guards";

import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Category emoji mapping
const categoryEmojis: Record<string, string> = {
  "ai-art": "🎨",
  "anime": "🎌",
  "3d-renders": "🎮",
  "character-design": "👤",
  "story-scriptwriting": "📖",
  "poetry": "✨",
  "blogging": "📝",
  "copywriting": "✍️",
  "chatgpt": "🤖",
  "midjourney": "🖼️",
  "stable-diffusion": "🎨",
  "coding-development": "💻",
  "gaming": "🎮",
  "music-prompts": "🎵",
  "motivation": "💪",
  "general-chat": "💬",
};

interface CreatorPromptCardProps {
  prompt: Prompt;
  onUpdate?: (id: string, updates: Partial<Prompt>) => void;
  onDelete?: (id: string) => void;
  onClick?: () => void;
}

export function CreatorPromptCard({
  prompt,
  onUpdate,
  onDelete,
  onClick,
}: CreatorPromptCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { requireAuth } = useGuestPermissions();
  const [copyCount, setCopyCount] = useState(prompt.copyCount || 0);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(prompt.comments || 0);

  const { isLiked, likeCount, handleLike } = usePromptActions(
    prompt,
    user,
    onUpdate,
    onDelete
  );

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!requireAuth("comment on posts")) {
      return;
    }
    setShowComments(!showComments);
  };

  const handleNewComment = () => {
    setCommentCount((prev) => prev + 1);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(prompt.content);
      await supabase.rpc("increment_copy_count", {
        prompt_id: toDatabaseValue(prompt.id),
      });
      setCopyCount((prev) => prev + 1);
      toast.success("Copied!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/users/${prompt.userId}`);
  };

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleLike(!isLiked);
  };

  // DEFENSIVE: Ensure category is never null/undefined before string operations
  const safeCategory = prompt.category || 'general-chat';
  const categoryEmoji = categoryEmojis[safeCategory] || "📌";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      onClick={onClick}
      className={cn(
        "group cursor-pointer",
        "bg-black/40 backdrop-blur-md rounded-2xl",
        "border border-white/10",
        "hover:border-brand-cyan/40",
        "hover:shadow-[0_0_30px_rgba(0,194,255,0.15)]",
        "transition-all duration-300"
      )}
    >
      {/* Remix Indicator */}
      {prompt.isRemix && (
        <div className="px-4 py-2 border-b border-brand-pink/20 bg-brand-pink/5 rounded-t-2xl">
          <div className="flex items-center gap-2 text-xs text-brand-pink">
            <GitBranch className="h-3 w-3" />
            <span>Remixed from @{prompt.originalUsername}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div
            className="flex items-center gap-3 cursor-pointer min-w-0 flex-shrink-0"
            onClick={handleUserClick}
          >
            <Avatar
              userId={prompt.userId}
              avatarId={prompt.avatarId}
              size="md"
            />
            <div className="min-w-0">
              <h4 className="font-semibold text-white/90 hover:text-brand-cyan transition-colors text-sm truncate">
                @{prompt.username}
              </h4>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <GeoQualityBadge 
              qualityScore={calculateGeoQualityScore(prompt)} 
              compact 
            />
            <Badge
              className={cn(
                "bg-brand-cyan/10 text-brand-cyan border-brand-cyan/20",
                "group-hover:bg-brand-cyan/20 transition-colors",
                "whitespace-nowrap text-xs max-w-[120px] sm:max-w-none truncate"
              )}
            >
              {categoryEmoji} {safeCategory.replace(/-/g, " ")}
            </Badge>
          </div>
        </div>

        {/* GEO Badges */}
        {prompt.geoMetadata && (
          <div className="mt-2 overflow-x-auto">
            <GeoBadges geoMetadata={prompt.geoMetadata} compact />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        {prompt.title && (
          <h3 className="font-bold text-white mb-2 line-clamp-2 group-hover:text-brand-cyan/90 transition-colors">
            {prompt.title}
          </h3>
        )}
        <p className="text-white/60 text-sm leading-relaxed line-clamp-3">
          {prompt.content}
        </p>
      </div>

      {/* Footer Actions */}
      <div className="px-4 pb-3 border-t border-white/5 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Like */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLikeClick}
              className="h-8 px-2 text-white/50 hover:text-brand-cyan hover:bg-brand-cyan/10"
            >
              <NeonUpvoteIcon
                className="h-4 w-4 mr-1"
                isActive={isLiked}
              />
              <span className={cn("text-xs", isLiked && "text-brand-cyan")}>
                {likeCount}
              </span>
            </Button>

            {/* Comment */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCommentClick}
              className={cn(
                "h-8 px-2 text-white/50 hover:text-brand-cyan hover:bg-brand-cyan/10",
                showComments && "text-brand-cyan bg-brand-cyan/10"
              )}
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1" />
              <span className="text-xs">{commentCount}</span>
            </Button>

            {/* Copy */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-8 px-2 text-white/50 hover:text-green-400 hover:bg-green-400/10"
            >
              <Copy className="h-3.5 w-3.5 mr-1" />
              <span className="text-xs">{copyCount}</span>
            </Button>
          </div>

          {/* GEO Reliability Actions */}
          {prompt.geoMetadata && (
            <div className="flex-shrink-0">
              <GeoReliabilityActions
                promptId={prompt.id}
                worksCount={prompt.geoMetadata.reliability?.community_verified || 0}
                issueCount={prompt.geoMetadata.reliability?.reported_issues || 0}
              />
            </div>
          )}
        </div>

        {/* Comment Preview Text */}
        <div 
          className="mt-3 flex items-center gap-2 text-xs text-white/40 cursor-pointer hover:text-white/60 transition-colors"
          onClick={handleCommentClick}
        >
          <MessageSquare className="h-3 w-3" />
          <span>{commentCount} Comments</span>
          <span className="text-brand-cyan/60">·</span>
          <span className="flex items-center gap-1 text-brand-cyan/60">
            <Sparkles className="h-3 w-3" />
            {showComments ? "Hide comments" : "Start a conversation"}
          </span>
        </div>
      </div>

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 bg-black/20">
              <PromptComments
                promptId={prompt.id}
                onNewComment={handleNewComment}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
